// Why people leave Fleetly: exits by month, by exit type and by primary reason,
// the average score for each rated question, and question 7 (the Driver
// Servant) averaged per manager. The signatories see all of it. A Driver Servant
// sees only question 7 for their own leavers, never an individual answer.
import { useEffect, type ReactNode } from 'react'
import { BarChart3 } from 'lucide-react'
import { useGetExitReport } from '../../hooks/backend/offboarding'
import { Spinner } from '../../lib/shadcn/spinner'
import { choiceLabel, exitTypeLabel, reasonLabel } from '../../utils/offboarding'

type Report = {
  ok: boolean
  scope: 'full' | 'own_manager_score'
  q7_by_servant: { servant: string | null; interviews: number; average: number | null }[]
  by_month?: { month: string; exits: number }[] | null
  by_exit_type?: { exit_type: string; exits: number }[] | null
  by_reason?: { reason: string; exits: number }[] | null
  scores?: { q_no: number; prompt: string; interviews: number; average: number | null }[] | null
  choices?: { q_no: number; choice: string; count: number }[] | null
  interviews?: { held: number; declined: number; offered_only: number } | null
}

function Bars({ rows }: { rows: { label: string; value: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No exits recorded yet.</p>
  return (
    <ul className="space-y-1">
      {rows.map((r) => (
        <li key={r.label} className="grid grid-cols-[10rem_1fr_2rem] items-center gap-2 text-xs">
          <span className="truncate text-foreground">{r.label}</span>
          <span className="h-2 rounded-full bg-muted"><span className="block h-2 rounded-full bg-[hsl(var(--brand))]" style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <span className="text-right tabular-nums text-muted-foreground">{r.value}</span>
        </li>
      ))}
    </ul>
  )
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  )
}

export function ExitReport() {
  const q = useGetExitReport<Report>()
  const load = q.trigger
  useEffect(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])

  if (q.error && !q.data) return <p className="p-6 text-sm text-muted-foreground">{q.error}</p>
  const r = q.data
  if (!r) return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the report…</p>

  const q7 = (
    <Card title="Question 7, the Driver Servant, averaged per manager">
      {r.q7_by_servant.length === 0 ? <p className="text-xs text-muted-foreground">No interview has scored it yet.</p> : (
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground"><tr><th className="py-1">Driver Servant</th><th>Interviews</th><th className="text-right">Average of 5</th></tr></thead>
          <tbody className="divide-y divide-border">
            {r.q7_by_servant.map((s) => (
              <tr key={s.servant ?? 'none'}><td className="py-1 text-foreground">{s.servant ?? 'Not on file'}</td><td className="tabular-nums">{s.interviews}</td>
                <td className="text-right tabular-nums">{s.average ?? '—'}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-[11px] text-muted-foreground">An average over one or two interviews can point to the person who gave it. Read small numbers with that in mind.</p>
    </Card>
  )

  if (r.scope !== 'full') {
    return <div className="mx-auto max-w-3xl space-y-4 p-4">{q7}</div>
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <BarChart3 className="h-4 w-4" />
        {r.interviews ? `${r.interviews.held} interviews held, ${r.interviews.declined} declined, ${r.interviews.offered_only} offered and not yet held.` : 'No interviews yet.'}
        {' Cancelled cases are left out.'}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Exits by month"><Bars rows={(r.by_month ?? []).map((m) => ({ label: m.month, value: m.exits }))} /></Card>
        <Card title="Exits by type"><Bars rows={(r.by_exit_type ?? []).map((m) => ({ label: exitTypeLabel(m.exit_type === 'not_recorded' ? null : m.exit_type), value: m.exits }))} /></Card>
        <Card title="Primary reason for leaving"><Bars rows={(r.by_reason ?? []).map((m) => ({ label: reasonLabel(m.reason === 'not_recorded' ? null : m.reason), value: m.exits }))} /></Card>
        <Card title="Average score per question">
          <ul className="space-y-1 text-xs">
            {(r.scores ?? []).map((s) => (
              <li key={s.q_no} className="flex justify-between gap-2">
                <span className="text-foreground">{s.q_no}. {s.prompt}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{s.average ?? '—'} ({s.interviews})</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card title="Answers to the choice questions">
        {(r.choices ?? []).length === 0 ? <p className="text-xs text-muted-foreground">None yet.</p> : (
          <ul className="grid gap-1 text-xs sm:grid-cols-2">
            {(r.choices ?? []).map((c) => (
              <li key={`${c.q_no}-${c.choice}`} className="flex justify-between gap-2">
                <span className="text-foreground">Q{c.q_no}: {choiceLabel(c.choice)}</span><span className="tabular-nums text-muted-foreground">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {q7}
    </div>
  )
}
